/**
 * `<wishlist-toggle>` Web Component.
 *
 * Click-toggles a product into / out of a local-storage wishlist. Emits
 * `lmdr:wishlist_updated` on `document` and asks the condition layer to
 * refresh any `condition="has-items-in-wishlist"` / `no-items-in-wishlist`
 * nodes.
 *
 * Authoring:
 *   <wishlist-toggle product-handle="my-product">…</wishlist-toggle>
 *   or, as a last-resort identity fallback:
 *   `<wishlist-toggle product-id="gid://shopify/Product/…">…</wishlist-toggle>`
 *
 * Persistence shape (localStorage key `lmdr_wishlist`):
 *   { version: 1, handles: ["product-handle-1", ...], expiresAt: epochMs }
 */

const STORAGE_KEY = "lmdr_wishlist";
const LEGACY_STORAGE_KEYS = ["smootify_wishlist"];
/** Business retention policy: 12 months, represented as 365 days. Not a legal maximum. */
export const WISHLIST_RETENTION_MS = 365 * 24 * 60 * 60 * 1000;
interface SavedWishlist { version: 1; handles: string[]; expiresAt: number }
let memoryWishlist: SavedWishlist | null = null;
let memoryOnly = false;

function normalizeHandles(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.filter((h): h is string => typeof h === "string")
    .map(h => h.trim()).filter(Boolean))];
}

function persist(value: SavedWishlist): void {
  memoryWishlist = value;
  try {
    if (value.handles.length) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    else window.localStorage.removeItem(STORAGE_KEY);
    // No second undated copy that could resurrect expired or removed products.
    for (const key of LEGACY_STORAGE_KEYS) window.localStorage.removeItem(key);
    memoryOnly = false;
  } catch { memoryOnly = true; /* Keep this tab usable if writes are blocked. */ }
}

function safeRead(): string[] {
  if (memoryOnly) return memoryWishlist && memoryWishlist.expiresAt > Date.now() ? [...memoryWishlist.handles] : [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    let value: unknown = null;
    try { value = raw ? JSON.parse(raw) : null; }
    catch { persist({ version: 1, handles: [], expiresAt: 0 }); return []; }
    if (value && !Array.isArray(value)) {
      const saved = value as Partial<SavedWishlist>;
      if (saved.version !== 1 || typeof saved.expiresAt !== "number" ||
          !Number.isFinite(saved.expiresAt) || saved.expiresAt <= Date.now() ||
          saved.expiresAt > Date.now() + WISHLIST_RETENTION_MS || !Array.isArray(saved.handles)) {
        persist({ version: 1, handles: [], expiresAt: 0 });
        return [];
      }
      memoryWishlist = { version: 1, handles: normalizeHandles(saved.handles), expiresAt: saved.expiresAt };
      return [...memoryWishlist.handles];
    }
    const legacy = LEGACY_STORAGE_KEYS.flatMap(key => {
      try { return normalizeHandles(JSON.parse(window.localStorage.getItem(key) ?? "null")); }
      catch { return []; }
    });
    const handles = normalizeHandles([...normalizeHandles(value), ...legacy]);
    if (handles.length) {
      // Old lists have no timestamp: one documented migration window from first read.
      persist({ version: 1, handles, expiresAt: Date.now() + WISHLIST_RETENTION_MS });
      return handles;
    }
    memoryWishlist = null;
    return [];
  } catch {
    return memoryWishlist && memoryWishlist.expiresAt > Date.now() ? [...memoryWishlist.handles] : [];
  }
}

function safeWrite(handles: string[]): void {
  persist({ version: 1, handles: normalizeHandles(handles), expiresAt: Date.now() + WISHLIST_RETENTION_MS });
}

let storageListenersAttached = false;
export function attachWishlistStorage(): void {
  if (storageListenersAttached) return;
  storageListenersAttached = true;
  let previous = JSON.stringify(safeRead());
  const refresh = (): void => {
    const handles = safeRead();
    const next = JSON.stringify(handles);
    if (next !== previous) { previous = next; notify(handles); }
  };
  document.addEventListener("lmdr:wishlist_updated", () => { previous = JSON.stringify(safeRead()); });
  window.addEventListener("storage", event => {
    if (event.key === null || [STORAGE_KEY, ...LEGACY_STORAGE_KEYS].includes(event.key)) refresh();
  });
  window.addEventListener("focus", refresh);
  document.addEventListener("visibilitychange", () => { if (!document.hidden) refresh(); });
  window.setInterval(refresh, 60_000);
}

export function getWishlistHandles(): string[] {
  return safeRead();
}

export function isInWishlist(handle: string): boolean {
  return safeRead().includes(handle);
}

export function toggleWishlistHandle(handle: string): boolean {
  const current = safeRead();
  const idx = current.indexOf(handle);
  if (idx >= 0) {
    const next = current.filter((h) => h !== handle);
    safeWrite(next);
    notify(next, { handle, added: false });
    return false;
  }
  const next = [...current, handle];
  safeWrite(next);
  notify(next, { handle, added: true });
  return true;
}

function notify(
  handles: string[],
  change?: { handle: string; added: boolean },
): void {
  if (typeof document === "undefined") return;

  // Dispatch-only : le listener `lmdr:wishlist_updated` dans
  // attributes/condition.ts gère le reapply document-wide en préservant
  // les autres flags du globalListState (hasRelated, hasCartUpsells,
  // lastViewedHandles).
  //
  // IMPORTANT — ne PAS appeler `applyConditions` directement ici : ça
  // passerait un state "amnésique" qui réinitialiserait tous les "has-*"
  // à false et ferait disparaître les blocs related / last-viewed /
  // cart-upsells en cascade à chaque clic sur un cœur wishlist.
  const detail = { handles, handle: change?.handle, added: change?.added };
  document.dispatchEvent(new CustomEvent("lmdr:wishlist_updated", { detail }));
  // Compat migration : certains snippets Webflow historiques écoutent encore
  // les events Smootify même si le runtime est maintenant LMDR.
  document.dispatchEvent(new CustomEvent("smootify:wishlist_updated", { detail }));
  if (change) {
    const addedName = change.added ? "wishlist:added" : "wishlist:removed";
    document.dispatchEvent(
      new CustomEvent(`lmdr:${addedName}`, {
        detail: { handle: change.handle, handles },
      }),
    );
    document.dispatchEvent(
      new CustomEvent(`smootify:${addedName}`, {
        detail: { handle: change.handle, handles },
      }),
    );
  }
}

// ---------------------------------------------------------------------------
//  Element
// ---------------------------------------------------------------------------

interface SmootifyProductLike extends HTMLElement {
  product?: { handle?: string; id?: string } | null;
}

export class WishlistToggleElement extends HTMLElement {
  private _onClick = (ev: Event): void => {
    ev.preventDefault();
    ev.stopPropagation();
    const handle = this.resolveHandle();
    if (!handle) {
      // eslint-disable-next-line no-console
      console.warn("[lmdr] wishlist-toggle: no handle resolved", this);
      return;
    }
    const added = toggleWishlistHandle(handle);
    this.reflectState(added);
    this.dispatchEvent(
      new CustomEvent("smootify:wishlist_toggle", {
        detail: { handle, added },
        bubbles: true,
      }),
    );
  };

  connectedCallback(): void {
    this.addEventListener("click", this._onClick);
    // Reflète l'état initial. Le handle peut ne pas être dispo encore (produit
    // pas chargé) — on re-check après `smootify:product_loaded`.
    this.reflectState(this.isActive());
    document.addEventListener("smootify:product_loaded", this._onProductLoaded);
    document.addEventListener("lmdr:wishlist_updated", this._onWishlistUpdated);
  }

  disconnectedCallback(): void {
    this.removeEventListener("click", this._onClick);
    document.removeEventListener("smootify:product_loaded", this._onProductLoaded);
    document.removeEventListener("lmdr:wishlist_updated", this._onWishlistUpdated);
  }

  private _onProductLoaded = (): void => {
    // Re-resolve le handle (peut être dispo maintenant via smootify-product.product).
    this.reflectState(this.isActive());
  };

  private _onWishlistUpdated = (): void => {
    this.reflectState(this.isActive());
  };

  // -------------------------------------------------------------------------
  //  Internals
  // -------------------------------------------------------------------------

  /**
   * Résolution du handle — cascade de fallbacks :
   *   1. Attribut `product-handle` explicite
   *   2. Attribut `data-handle`
   *   3. Le produit chargé par le <smootify-product> ancestor (handle)
   *   4. Le `data-handle` de l'ancêtre smootify-product (fallback pré-load)
   *   5. L'URL si on est sur une page produit
   *   6. Attribut `product-id` / `data-product-id` / `data-id`
   *      (dernier recours seulement : la page wishlist rend les handles)
   */
  private resolveHandle(): string | null {
    const direct =
      this.getAttribute("product-handle") ??
      this.getAttribute("data-handle") ??
      this.getAttribute("handle");
    if (direct && direct.length > 0) return direct;

    const host = this.closest<SmootifyProductLike>("smootify-product");
    if (host) {
      // Produit déjà chargé
      const loadedHandle = host.product?.handle;
      if (loadedHandle) return loadedHandle;
      // Fallback pré-load : l'attribut data-handle du host (set sur les clones multi).
      const hostHandle = host.getAttribute("data-handle");
      if (hostHandle && hostHandle.length > 0) return hostHandle;
    }

    // Fallback URL (si on est sur une page produit, le wishlist-toggle sans
    // ancêtre smootify-product utilise l'URL).
    if (typeof window !== "undefined") {
      const segments = window.location.pathname.split("/").filter(Boolean);
      // /product/<handle> ou /<locale>/product/<handle>
      const idx = segments.indexOf("product");
      if (idx >= 0 && segments[idx + 1]) {
        try {
          return decodeURIComponent(segments[idx + 1]!);
        } catch {
          return segments[idx + 1] ?? null;
        }
      }
    }

    const directIdentity =
      this.getAttribute("product-id") ??
      this.getAttribute("data-product-id") ??
      this.getAttribute("data-id");
    if (
      directIdentity &&
      directIdentity.length > 0 &&
      !["wishlist", "related", "last-viewed", "cart-upsells", "filter"].includes(directIdentity)
    ) {
      return directIdentity;
    }

    return null;
  }

  private isActive(): boolean {
    const h = this.resolveHandle();
    return h ? isInWishlist(h) : false;
  }

  private reflectState(active: boolean): void {
    this.classList.toggle("is-in-wishlist", active);
    this.setAttribute("aria-pressed", active ? "true" : "false");
  }
}
